import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BuildIcon from "@mui/icons-material/Build";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import FlightIcon from "@mui/icons-material/Flight";
import WarningIcon from "@mui/icons-material/Warning";
import TrafficIcon from "@mui/icons-material/Traffic";
import TowingIcon from "@mui/icons-material/LocalShipping";
import FlashlightOnIcon from "@mui/icons-material/FlashlightOn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import api from "../api/client";
import {
  fileToDataUrl,
  loadProfile,
  saveProfile,
  PROFILE_SYNC_EVENT,
} from "../utils/profileStorage";
import trainingModules from "../constants/trainingModules";

// All available awards from the system
const ALL_AWARDS = [
  { id: "founding-member", title: "Founding Member", icon: "🏛️", category: "recognition", description: "Recognizes those who laid the foundation of the Texas Highway Rangers and shaped its mission from day one." },
  { id: "fema-ics-certified", title: "FEMA ICS Certified", icon: "🎓", category: "certification", description: "Issued upon completion of core FEMA Incident Command System training, demonstrating readiness for emergency coordination and disaster response." },
  { id: "national-tim-certified", title: "National TIM Training Certified", icon: "🚧", category: "certification", description: "Awarded to members who have completed the National Traffic Incident Management Responder Training Program, focused on safe, coordinated, and quick clearance of roadway incidents." },
  { id: "roadway-leadership-certified", title: "Roadway Incident Response Leadership Certified", icon: "👔", category: "certification", description: "Awarded upon successful completion of the leadership program covering all critical aspects of roadway incident response. This certification is required for those serving as region leaders or training instructors." },
  { id: "runway-responder", title: "Runway Responder", icon: "🦺", category: "achievement", description: "Because nobody rocks ANSI Class III quite like you. Safety… but make it stylish." },
  { id: "jumpstart-jedi", title: "Jumpstart Jedi", icon: "⚡", category: "achievement", description: "Uses the Force (and proper polarity) to bring the dead… batteries… back to life." },
  { id: "lug-nut-whisperer", title: "Lug Nut Whisperer", icon: "🔧", category: "achievement", description: "Awarded for mastering the art of the 4-way lug wrench and always knowing which way is tight." },
  { id: "field-repair-wizard", title: "Field Repair Wizard", icon: "🧙", category: "achievement", description: "Presented to the Ranger who fixed something critical with zip ties, duct tape, or sheer willpower." },
  { id: "wrong-place-perfect-time", title: "Wrong Place, Perfect Time", icon: "🎯", category: "achievement", description: "You weren't trying to find the incident. It found you." },
  { id: "rope-wrangler", title: "Rope Wrangler", icon: "🪢", category: "achievement", description: "For expertly pulling stranded vehicles with nothing but a tow rope, horsepower, and a healthy disregard for getting stuck yourself." },
  { id: "bumper-buddy", title: "Bumper Buddy", icon: "🚗", category: "achievement", description: "For providing the most gentle shoves in public safety — like a hug, but with 4,000 pounds of steel." },
  { id: "recovery-agent", title: "Recovery Agent", icon: "🛻", category: "achievement", description: "Not every off-road adventure was planned. When tires leave the pavement, you're the one who gets them home." },
  { id: "cooler-command", title: "Cooler Command", icon: "💧", category: "achievement", description: "You're not just providing water — you're delivering comfort, calm, and compassion when people need it most. Proud provider of liquid morale on hot Texas roads." },
  { id: "gear-goblin", title: "Gear Goblin", icon: "👺", category: "gear", description: "For the Ranger who owns more lights, tools, and gear than some fire departments — and somehow still finds a reason to buy more. You've officially entered goblin territory. Welcome to the hoard." },
  { id: "supply-dragon", title: "Supply Dragon", icon: "🐉", category: "gear", description: "Your trunk is now a rolling armory. You don't just collect gear — you guard it. Awarded to the Ranger whose supply stash rivals a tactical outfitter's warehouse." },
  { id: "lord-of-the-loadout", title: "Lord of the Loadout", icon: "👑", category: "gear", description: "You are the final boss of roadside readiness. From tools to trauma kits, batteries to bolt cutters — if it exists, you already bought two. One to use. One just in case." },
  { id: "public-safety-partner", title: "Public Safety Partner", icon: "🛡️", category: "recognition", description: "Awarded to Rangers who actively serve in law enforcement, fire, EMS, dispatch, or emergency management outside of the Texas Highway Rangers. Your dedication to public safety doesn't stop with us — it's a calling you live out every day." },
  { id: "1-year-service", title: "1 Year Service", icon: "🎖️", category: "service", description: "For the Rangers who've been with us through all four seasons — rain, shine, heat waves, and Texas traffic. One year stronger, one year prouder." },
  { id: "2-year-service", title: "2 Year Service", icon: "🎖️", category: "service", description: "Two years of dedication, two years of showing up when it counts. You're not just a Ranger — you're a cornerstone." },
  { id: "3-year-service", title: "3 Year Service", icon: "🎖️", category: "service", description: "Three years on the road, countless assists in the rearview. Your commitment inspires the next generation of Rangers." },
  { id: "4-year-service", title: "4 Year Service", icon: "🎖️", category: "service", description: "Four years of service, four years of being the calm in someone's storm. A veteran in every sense of the word." },
  { id: "5-year-service", title: "5 Year Service", icon: "🏆", category: "service", description: "Half a decade of selfless service. You've seen it all, done it all, and still show up ready to help. A true pillar of the Texas Highway Rangers." },
  { id: "10-year-service", title: "10 Year Service", icon: "🏅", category: "service", description: "A decade of dedication. Through good times and hard times, you've been a constant presence on Texas roads. Legend status achieved." },
  { id: "lone-star-award", title: "Lone Star Award", icon: "⭐", category: "recognition", description: "Yearly award given to the Ranger who embodies what it means to serve with excellence. Tireless, humble, dependable. A true light on the roadside and a leader among peers." },
  { id: "critical-response-commendation", title: "Critical Response Commendation", icon: "💗", category: "recognition", description: "Recognizing decisive action and unwavering composure during a critical incident — when every second counted. Awarded to the Ranger who preserved what matters most: another chance, another heartbeat, another tomorrow." },
  { id: "target-acquired", title: "Target Acquired", icon: "🔭", category: "achievement", description: "You've developed a sixth sense for spotting stranded drivers — with this many confirmed sightings, you might as well be flying a drone. Your sharp eyes help the team prioritize real needs over empty hazards and keep our response focused where it matters most." },
  { id: "fuel-fairy", title: "Fuel Fairy", icon: "🛢️", category: "achievement", description: "When the tank hits empty, you're the one who fills it back up. Guardian angel of the gas-less." },
  { id: "traffic-tamer", title: "Traffic Tamer", icon: "🚦", category: "achievement", description: "Cones, flares, and hand signals — you control the chaos so others can pass safely." },
  { id: "road-scholar", title: "Road Scholar", icon: "📚", category: "achievement", description: "You don't just respond — you diagnose. Part mechanic, part detective." },
  { id: "century-club", title: "Century Club", icon: "💯", category: "achievement", description: "100 incidents. Triple digits. You're officially in the club." },
  { id: "mentor-badge", title: "Mentor Badge", icon: "👨‍🏫", category: "recognition", description: "You don't just serve — you teach. Awarded for guiding new Rangers through their first responses and sharing the wisdom that keeps us all safer." },
  { id: "air-ace", title: "Air Ace", icon: "🎈", category: "achievement", description: "Keeping tires inflated and spirits high. You've mastered the art of the perfect PSI." },
  { id: "good-neighbor", title: "Good Neighbor", icon: "🚐", category: "achievement", description: "Sometimes people just need a lift. You're the one who gives it — no questions asked, just kindness." },
  { id: "fire-fighter", title: "Fire Fighter", icon: "🧯", category: "achievement", description: "Cool under pressure, literally. When flames threaten, you're ready with the extinguisher." },
  { id: "master-mechanic", title: "Master Mechanic", icon: "🔩", category: "achievement", description: "When minor won't cut it, you go major. Complex repairs don't scare you — they challenge you." },
  { id: "debris-destroyer", title: "Debris Destroyer", icon: "🧹", category: "achievement", description: "From mattresses to mufflers, you keep Texas roads clear. One piece of debris at a time." },
  { id: "bridge-builder", title: "Bridge Builder", icon: "🌉", category: "achievement", description: "Language barriers don't stop you from helping. You bridge the gap when communication matters most." },
  { id: "motor-one-moving", title: "Motor One Moving", icon: "🚨", category: "achievement", description: "Guiding the vulnerable safely to their destination. When someone needs an escort, you lead the way." },
  { id: "hat-trick", title: "Hat Trick", icon: "🎩", category: "achievement", description: "Three incidents in one day? That's a hat trick. And you've done it more than once." },
  { id: "iron-ranger", title: "Iron Ranger", icon: "🦾", category: "recognition", description: "500 career incidents. You're not just part of the team — you are the team. Through heat, storms, holidays, and late nights, you've answered the call half a thousand times. Iron doesn't bend. Neither do you." },
  { id: "community-ambassador", title: "Community Ambassador", icon: "🤝", category: "recognition", description: "Awarded to the Ranger who goes beyond the roadside to represent the Highway Rangers at public events, community outreach programs, and educational demonstrations. You're the face of the mission." },
  { id: "recruiter", title: "Recruiter", icon: "📣", category: "recognition", description: "Every great organization grows because someone believes in the mission enough to share it. You've brought new Rangers into the fold, growing our ranks and strengthening our impact." },
  { id: "above-and-beyond", title: "Above & Beyond", icon: "🌟", category: "recognition", description: "When the situation called for more, you gave everything. This award recognizes a single incident where your actions went far beyond what was expected — selfless, decisive, and extraordinary." },
  { id: "unit-citation", title: "Unit Citation", icon: "🎗️", category: "recognition", description: "Awarded to Rangers who participated in a multi-unit response that received a formal commendation. When the team comes together under pressure and delivers exceptional results, everyone on scene earns this ribbon." },
  { id: "night-owl", title: "Night Owl", icon: "🦉", category: "achievement", description: "While most people are asleep, you're out on the road keeping Texas safe. The night shift isn't for everyone — but for you, it's where you shine brightest." },
  { id: "weekend-warrior", title: "Weekend Warrior", icon: "🗓️", category: "achievement", description: "Saturdays and Sundays are for rest — unless you're a Ranger. You sacrifice your weekends so that stranded motorists don't have to wait until Monday for help." },
  { id: "highway-guardian", title: "Highway Guardian", icon: "🛣️", category: "achievement", description: "250 incidents in a single year. You don't just patrol the highway — you guard it. An extraordinary level of commitment that puts you in a class of your own." },
  { id: "swiss-army-ranger", title: "Swiss Army Ranger", icon: "🇨🇭", category: "achievement", description: "Jumpstarts, tire changes, fuel deliveries, traffic control, towing — you do it all. A true jack-of-all-trades who's ready for anything the road throws at you." },
  { id: "holiday-hero", title: "Holiday Hero", icon: "🎄", category: "achievement", description: "While families gather and fireworks fly, you're out on the road keeping people safe. Thanksgiving, Christmas, New Year's, the Fourth — you show up when it matters most." },
  { id: "pacemaker", title: "Pacemaker", icon: "💓", category: "achievement", description: "Consistency is the hallmark of a true professional. Thirty straight days of responding to at least one incident — rain or shine, weekday or weekend. You never miss a beat." },
  { id: "500-career-incidents", title: "500 Career Incidents", icon: "🎯", category: "service", description: "Half a thousand calls answered. This milestone represents years of dedication, countless hours on the road, and an unwavering commitment to serving others." },
  { id: "1000-career-incidents", title: "1,000 Career Incidents", icon: "🏛️", category: "service", description: "One thousand incidents. A number that speaks for itself. You are a pillar of the Texas Highway Rangers and a living legend of roadside service." },
];

const EQUIPMENT_OPTIONS = [
  { label: "Emergency Lighting & Beacons", value: "lighting", icon: <FlashlightOnIcon /> },
  { label: "Medical / Trauma Kit", value: "medical", icon: <MedicalServicesIcon /> },
  { label: "Fire Suppression / Extinguisher", value: "fire", icon: <LocalFireDepartmentIcon /> },
  { label: "Vehicle Recovery / Winch", value: "recovery", icon: <TowingIcon /> },
  { label: "Traffic Control Kit", value: "traffic", icon: <TrafficIcon /> },
  { label: "Drone / Aerial Overwatch", value: "drone", icon: <FlightIcon /> },
  { label: "HazMat / Special Hazards", value: "hazmat", icon: <WarningIcon /> },
];

const EMPTY_VEHICLE_FORM = {
  year: "",
  make: "",
  model: "",
  trim: "",
  color: "",
  mileage: "",
  licensePlate: "",
  notes: "",
  photoDataUrl: "",
};

const normalizeVehicleEntry = (vehicle, fallbackId) => {
  if (!vehicle) {
    return null;
  }
  const asString = (value) => (value === undefined || value === null ? "" : String(value).trim());
  return {
    id: vehicle.id || fallbackId || `vehicle-${Date.now()}`,
    year: asString(vehicle.year),
    make: asString(vehicle.make),
    model: asString(vehicle.model || vehicle.type),
    trim: asString(vehicle.trim),
    color: asString(vehicle.color),
    mileage: asString(vehicle.mileage),
    licensePlate: asString(vehicle.licensePlate),
    notes: asString(vehicle.notes),
    photoDataUrl: asString(vehicle.photoDataUrl),
  };
};

export default function Profile() {
  const { user, refreshUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(() => loadProfile());
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [busyField, setBusyField] = useState("none");
  const [completedTrainingIds, setCompletedTrainingIds] = useState([]);
  const [trainingCertifications, setTrainingCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Directory view states
  const [viewMode, setViewMode] = useState("directory"); // "directory" or "profile"
  const [allRangers, setAllRangers] = useState([]);
  const [selectedRangerId, setSelectedRangerId] = useState(null); // null = viewing own profile
  const [viewedProfile, setViewedProfile] = useState(null); // Profile data when viewing another ranger
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingDirectory, setLoadingDirectory] = useState(true);
  const [loadingViewedProfile, setLoadingViewedProfile] = useState(false);
  
  // Cover reposition states
  const [repositioning, setRepositioning] = useState(false);
  const [dragStartY, setDragStartY] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(50);
  const [tempCoverPosY, setTempCoverPosY] = useState(null);
  const coverRef = useRef(null);
  const coverImgRef = useRef(null);

  // Dialog states
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [awardDialogOpen, setAwardDialogOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Form states
  const [profileForm, setProfileForm] = useState({
    displayName: "",
    callSign: "",
    phoneNumber: "",
    email: "",
    bio: "",
    joinDate: "",
    motto: "",
  });
  const [vehicleForm, setVehicleForm] = useState({ ...EMPTY_VEHICLE_FORM });
  const [vehicleError, setVehicleError] = useState("");
  const [editingVehicleId, setEditingVehicleId] = useState(null);

  // Admin award grant state
  const [grantYear, setGrantYear] = useState(new Date().getFullYear());
  const [grantNotes, setGrantNotes] = useState("");
  const [grantingAwardId, setGrantingAwardId] = useState(null);

  // Load profile from API on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Add cache-busting to prevent browser caching
        const response = await api.get(`/profiles/me?_t=${Date.now()}`);
        const serverProfile = response.data;
        console.log("[Profile] Loaded from server:", {
          hasProfilePhoto: !!serverProfile.profilePhotoDataUrl,
          hasBackgroundPhoto: !!serverProfile.backgroundPhotoDataUrl,
          backgroundPhotoLength: serverProfile.backgroundPhotoDataUrl?.length || 0
        });
        // Merge server data with local storage (server takes precedence)
        const mergedProfile = {
          ...loadProfile(),
          displayName: serverProfile.displayName || "",
          callSign: serverProfile.callSign || "",
          pronouns: serverProfile.pronouns || "",
          timezone: serverProfile.timezone || "Central",
          bio: serverProfile.bio || "",
          motto: serverProfile.motto || "",
          joinDate: serverProfile.joinDate || "",
          profilePhotoDataUrl: serverProfile.profilePhotoDataUrl || "",
          backgroundPhotoDataUrl: serverProfile.backgroundPhotoDataUrl || "",
          coverPositionY: serverProfile.coverPositionY ?? 50,
          awards: serverProfile.awards || [],
          earnedAwards: serverProfile.earnedAwards || [],
          vehicles: serverProfile.vehicles || [],
          equipmentCapabilities: serverProfile.equipmentCapabilities || [],
          phoneNumber: serverProfile.phoneNumber || "",
          email: serverProfile.email || "",
        };
        setProfile(mergedProfile);
        console.log("[Profile] Set merged profile:", {
          hasBackgroundPhoto: !!mergedProfile.backgroundPhotoDataUrl,
          backgroundPhotoLength: mergedProfile.backgroundPhotoDataUrl?.length || 0
        });
        // Save to localStorage WITHOUT photos (to avoid quota issues)
        // Use saveProfile (not updateProfile) to fully replace localStorage data
        const localStorageProfile = { 
          ...mergedProfile,
          profilePhotoDataUrl: "",
          backgroundPhotoDataUrl: ""
        };
        saveProfile(localStorageProfile);
      } catch (error) {
        console.error("Failed to load profile from server:", error);
        // Fall back to localStorage
        setProfile(loadProfile());
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // Load all rangers for the directory view
  useEffect(() => {
    const fetchAllRangers = async () => {
      try {
        const response = await api.get("/profiles/all");
        const rangers = (response.data || []).map(user => ({
          userId: user.userId,
          username: user.username,
          rangerId: user.rangerId || "",
          displayName: user.displayName || user.username || "",
          callSign: user.callSign || "",
          division: user.division || "",
          role: user.role || "ranger",
          profilePhotoDataUrl: user.profilePhotoDataUrl || "",
        }));
        setAllRangers(rangers);
      } catch (error) {
        console.error("Failed to load ranger directory:", error);
      } finally {
        setLoadingDirectory(false);
      }
    };
    fetchAllRangers();
  }, []);

  // Load a specific ranger's profile when selected
  const handleViewRangerProfile = useCallback(async (ranger) => {
    // If viewing own profile
    if (ranger.userId === user?.id) {
      setSelectedRangerId(null);
      setViewedProfile(null);
      setViewMode("profile");
      return;
    }

    setLoadingViewedProfile(true);
    setSelectedRangerId(ranger.userId);
    setViewMode("profile");
    
    try {
      const response = await api.get(`/profiles/${ranger.userId}`);
      setViewedProfile(response.data);
    } catch (error) {
      console.error("Failed to load ranger profile:", error);
      setSnackbar({ open: true, message: "Failed to load profile", severity: "error" });
      setViewMode("directory");
    } finally {
      setLoadingViewedProfile(false);
    }
  }, [user?.id]);

  const handleBackToDirectory = useCallback(() => {
    setViewMode("directory");
    setSelectedRangerId(null);
    setViewedProfile(null);
  }, []);

  // Filter rangers by search query
  const filteredRangers = useMemo(() => {
    if (!searchQuery.trim()) return allRangers;
    const query = searchQuery.toLowerCase();
    return allRangers.filter((ranger) =>
      (ranger.displayName || "").toLowerCase().includes(query) ||
      (ranger.rangerId || "").toLowerCase().includes(query) ||
      (ranger.callSign || "").toLowerCase().includes(query) ||
      (ranger.division || "").toLowerCase().includes(query)
    );
  }, [allRangers, searchQuery]);

  // Removed the PROFILE_SYNC_EVENT listener - we manage state directly now

  // Fetch training certifications from server
  useEffect(() => {
    const fetchTrainingCerts = async () => {
      try {
        // If viewing another user, fetch their approved certs; otherwise fetch own (all statuses)
        const endpoint = selectedRangerId
          ? `/training-certifications/user/${selectedRangerId}`
          : "/training-certifications/my";
        const res = await api.get(endpoint);
        const certs = res.data || [];
        setTrainingCertifications(certs);
        // Derive completed IDs (only approved count)
        setCompletedTrainingIds(
          certs.filter((c) => c.status === "approved").map((c) => c.courseId)
        );
      } catch (err) {
        console.error("Failed to load training certifications:", err);
      }
    };
    fetchTrainingCerts();
  }, [selectedRangerId]);

  const persistProfile = useCallback(async (payload, options = {}) => {
    // Merge with current state (not localStorage) to preserve server data
    const next = { ...profile, ...payload };
    setProfile(next);
    
    // Save to localStorage (without large photos to avoid quota issues)
    // Use saveProfile (not updateProfile) to fully replace localStorage data
    const localStoragePayload = { 
      ...next,
      profilePhotoDataUrl: "",
      backgroundPhotoDataUrl: ""
    };
    saveProfile(localStoragePayload);
    
    // Save to server - send actual values, not null for empty strings
    try {
      const apiPayload = {
        displayName: next.displayName || undefined,
        callSign: next.callSign || undefined,
        pronouns: next.pronouns || undefined,
        timezone: next.timezone || undefined,
        bio: next.bio || undefined,
        motto: next.motto || undefined,
        joinDate: next.joinDate || undefined,
        awards: next.awards || [],
        vehicles: next.vehicles || [],
        equipmentCapabilities: next.equipmentCapabilities || [],
      };
      
      // Include photo fields
      if (next.profilePhotoDataUrl) {
        apiPayload.profilePhotoDataUrl = next.profilePhotoDataUrl;
      }
      if (next.backgroundPhotoDataUrl) {
        apiPayload.backgroundPhotoDataUrl = next.backgroundPhotoDataUrl;
      }
      if (next.coverPositionY !== undefined) {
        apiPayload.coverPositionY = next.coverPositionY;
      }
      
      console.log("Saving profile to server, payload keys:", Object.keys(apiPayload), "bgPhoto length:", next.backgroundPhotoDataUrl?.length || 0);
      
      const response = await api.put("/profiles/me", apiPayload);
      console.log("Profile saved successfully:", response.status);
      
      if (!options.silent) {
        setSnackbar({ open: true, message: "Profile updated", severity: "success" });
      }
    } catch (error) {
      console.error("Failed to save profile to server:", error);
      console.error("Error details:", error.response?.data, error.response?.status);
      if (!options.silent) {
        setSnackbar({ open: true, message: "Profile saved locally (server sync failed)", severity: "warning" });
      }
    }
    
    return next;
  }, [profile]);

  const profileName = profile.displayName || user?.name || user?.username || "Ranger";
  const callSign = user?.rangerId || profile.callSign || "";
  const vehicles = useMemo(() => {
    return (profile.vehicles || [])
      .map((vehicle, index) => normalizeVehicleEntry(vehicle, `vehicle-${index}`))
      .filter(Boolean);
  }, [profile.vehicles]);
  const equipmentCapabilities = profile.equipmentCapabilities || [];
  const earnedAwards = profile.earnedAwards || [];

  const completedCourses = useMemo(() => {
    const completedSet = new Set(completedTrainingIds);
    return trainingModules.filter((module) => completedSet.has(module.id));
  }, [completedTrainingIds]);
  const totalCourses = trainingModules.length;
  const completedCourseCount = completedCourses.length;
  const trainingPercent = totalCourses === 0 ? 0 : Math.round((completedCourseCount / totalCourses) * 100);

  // Get award details by ID
  const getAwardDetails = (awardId) => ALL_AWARDS.find(a => a.id === awardId) || { title: awardId, icon: "🏅" };

  // Cover position value (computed after displayProfile is available)

  // Cover reposition handlers
  const handleStartReposition = () => {
    setTempCoverPosY(profile.coverPositionY ?? 50);
    setRepositioning(true);
  };

  const handleCancelReposition = () => {
    setTempCoverPosY(null);
    setRepositioning(false);
  };

  const handleSaveReposition = async () => {
    await persistProfile({ coverPositionY: tempCoverPosY ?? 50 }, { silent: true });
    setTempCoverPosY(null);
    setRepositioning(false);
    setSnackbar({ open: true, message: "Cover position saved", severity: "success" });
  };

  const handleCoverPointerDown = (e) => {
    if (!repositioning) return;
    e.preventDefault();
    setDragStartY(e.clientY);
    setDragStartPos(tempCoverPosY ?? 50);
    coverRef.current?.setPointerCapture(e.pointerId);
  };

  const handleCoverPointerMove = (e) => {
    if (!repositioning || dragStartY === null) return;
    e.preventDefault();
    const containerHeight = coverRef.current?.offsetHeight || 240;
    const imgEl = coverImgRef.current;
    const imgNaturalRatio = imgEl ? imgEl.naturalHeight / imgEl.naturalWidth : 1;
    const containerWidth = coverRef.current?.offsetWidth || 800;
    const renderedImgHeight = containerWidth * imgNaturalRatio;
    const overflow = renderedImgHeight - containerHeight;
    if (overflow <= 0) return; // image fits, no repositioning needed
    const deltaY = e.clientY - dragStartY;
    const deltaPct = (deltaY / overflow) * 100;
    const newPos = Math.min(100, Math.max(0, dragStartPos - deltaPct));
    setTempCoverPosY(newPos);
  };

  const handleCoverPointerUp = (e) => {
    if (!repositioning) return;
    setDragStartY(null);
    coverRef.current?.releasePointerCapture(e.pointerId);
  };

  // Handlers
  const handleImageUpload = (field) => async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusyField(field);
    try {
      const dataUrl = await fileToDataUrl(file);
      console.log("Image converted, field:", field, "dataUrl length:", dataUrl?.length);
      await persistProfile({ [field]: dataUrl });
    } catch (error) {
      console.error("Image upload error:", error);
      setSnackbar({ open: true, message: error.message || "Unable to process image", severity: "error" });
    } finally {
      setBusyField("none");
    }
  };

  const handleOpenEditProfile = () => {
    setProfileForm({
      displayName: profile.displayName || user?.name || "",
      callSign: user?.rangerId || profile.callSign || "",
      phoneNumber: profile.phoneNumber || user?.phoneNumber || "",
      email: profile.email || user?.email || "",
      bio: profile.bio || "",
      joinDate: profile.joinDate || "",
      motto: profile.motto || "",
    });
    setEditProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      if (user) {
        await api.put("/users/profile", {
          name: profileForm.displayName || null,
          email: profileForm.email || null,
          phoneNumber: profileForm.phoneNumber || null,
          rangerId: profileForm.callSign || null,
          division: user?.division || null,
        });
        await refreshUser?.();
      }
      persistProfile(profileForm, { silent: true });
      setSnackbar({ open: true, message: "Profile updated", severity: "success" });
      setEditProfileOpen(false);
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to update profile.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEquipmentToggle = (value) => () => {
    const nextSet = new Set(equipmentCapabilities);
    if (nextSet.has(value)) {
      nextSet.delete(value);
    } else {
      nextSet.add(value);
    }
    persistProfile({ equipmentCapabilities: Array.from(nextSet) });
  };

  const handleVehiclePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setVehicleForm(prev => ({ ...prev, photoDataUrl: dataUrl }));
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Unable to process image", severity: "error" });
    }
  };

  const handleSaveVehicle = () => {
    const trimmedMake = vehicleForm.make.trim();
    const trimmedModel = vehicleForm.model.trim();
    const trimmedTrim = vehicleForm.trim?.trim() || "";
    if (!trimmedMake && !trimmedModel) {
      setVehicleError("Add at least a make or model.");
      return;
    }
    const preparedVehicle = {
      id: vehicleForm.id || `vehicle-${Date.now()}`,
      ...vehicleForm,
      make: trimmedMake,
      model: trimmedModel,
      trim: trimmedTrim,
    };
    // If editing, keep position; if new, add to front
    let updatedVehicles;
    if (editingVehicleId) {
      updatedVehicles = vehicles.map((v) => (v.id === editingVehicleId ? preparedVehicle : v));
    } else {
      updatedVehicles = [preparedVehicle, ...vehicles];
    }
    persistProfile({ vehicles: updatedVehicles });
    setVehicleForm({ ...EMPTY_VEHICLE_FORM });
    setVehicleError("");
    setEditingVehicleId(null);
    setVehicleDialogOpen(false);
    setSnackbar({ open: true, message: editingVehicleId ? "Vehicle updated" : "Vehicle added", severity: "success" });
  };

  const handleRemoveVehicle = (vehicleId) => () => {
    const updatedVehicles = vehicles.filter((v) => v.id !== vehicleId);
    persistProfile({ vehicles: updatedVehicles });
    setSnackbar({ open: true, message: "Vehicle removed", severity: "success" });
  };

  // Admin: grant award to viewed profile
  const handleGrantAward = useCallback(async (awardId, year, notes) => {
    if (!selectedRangerId) return;
    try {
      const res = await api.put(`/profiles/${selectedRangerId}/earned-awards`, {
        awardId,
        year: year || new Date().getFullYear(),
        notes: notes || null,
        revoke: false,
      });
      // Update viewedProfile with new earned awards
      setViewedProfile((prev) => prev ? { ...prev, earnedAwards: res.data } : prev);
      setSnackbar({ open: true, message: "Award granted", severity: "success" });
      setGrantingAwardId(null);
      setGrantNotes("");
    } catch (error) {
      console.error("Failed to grant award:", error);
      setSnackbar({ open: true, message: "Failed to grant award", severity: "error" });
    }
  }, [selectedRangerId]);

  // Admin: revoke award from viewed profile
  const handleRevokeAward = useCallback(async (awardId) => {
    if (!selectedRangerId) return;
    try {
      const res = await api.put(`/profiles/${selectedRangerId}/earned-awards`, {
        awardId,
        year: 0,
        revoke: true,
      });
      setViewedProfile((prev) => prev ? { ...prev, earnedAwards: res.data } : prev);
      setSnackbar({ open: true, message: "Award revoked", severity: "success" });
    } catch (error) {
      console.error("Failed to revoke award:", error);
      setSnackbar({ open: true, message: "Failed to revoke award", severity: "error" });
    }
  }, [selectedRangerId]);

  // Stats calculation
  const stats = useMemo(() => ({
    awards: earnedAwards.length,
    vehicles: vehicles.length,
    equipment: equipmentCapabilities.length,
    training: completedCourseCount,
  }), [earnedAwards.length, vehicles.length, equipmentCapabilities.length, completedCourseCount]);

  // Determine which profile to display (own or viewed)
  const isViewingOwnProfile = viewMode === "profile" && !selectedRangerId;
  const displayProfile = isViewingOwnProfile ? profile : viewedProfile;
  const displayName = isViewingOwnProfile 
    ? (profile.displayName || user?.name || user?.username || "Ranger")
    : (viewedProfile?.displayName || viewedProfile?.username || "Ranger");
  const displayCallSign = isViewingOwnProfile
    ? (user?.rangerId || profile.callSign || "")
    : (viewedProfile?.rangerId || viewedProfile?.callSign || "");
  const displayDivision = isViewingOwnProfile
    ? (user?.division || "Unassigned")
    : (viewedProfile?.division || "Unassigned");
  const displayEarnedAwards = isViewingOwnProfile ? earnedAwards : (viewedProfile?.earnedAwards || []);
  const displayVehicles = isViewingOwnProfile ? vehicles : (viewedProfile?.vehicles || []).map((v, i) => normalizeVehicleEntry(v, `vehicle-${i}`)).filter(Boolean);
  const displayEquipment = isViewingOwnProfile ? equipmentCapabilities : (viewedProfile?.equipmentCapabilities || []);
  const coverPosY = tempCoverPosY ?? displayProfile?.coverPositionY ?? 50;

  if (loading || loadingDirectory) {
    return (
      <DashboardLayout title="Ranger Directory">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <LinearProgress sx={{ width: 200 }} />
        </Box>
      </DashboardLayout>
    );
  }

  // Directory View
  if (viewMode === "directory") {
    return (
      <DashboardLayout title="Ranger Directory">
        <Stack spacing={3}>
          {/* Header */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GroupIcon color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Ranger Directory
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {allRangers.length} rangers • Click on a ranger to view their profile
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search rangers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <Button
                  variant="contained"
                  startIcon={<PersonIcon />}
                  onClick={() => { setSelectedRangerId(null); setViewedProfile(null); setViewMode("profile"); }}
                >
                  My Profile
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Rangers Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Ranger</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ranger ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Division</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRangers.map((ranger) => (
                  <TableRow
                    key={ranger.userId}
                    hover
                    onClick={() => handleViewRangerProfile(ranger)}
                    sx={{ 
                      cursor: "pointer",
                      backgroundColor: ranger.userId === user?.id ? "action.selected" : "transparent",
                      "&:hover": { backgroundColor: "action.hover" }
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={ranger.profilePhotoDataUrl}
                          sx={{ width: 40, height: 40 }}
                        >
                          {(ranger.displayName || ranger.username || "R").slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ranger.displayName || ranger.username || "Unknown"}
                            {ranger.userId === user?.id && (
                              <Chip label="You" size="small" color="primary" sx={{ ml: 1, height: 20 }} />
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {ranger.rangerId || ranger.callSign || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ranger.division || "Unassigned"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ranger.role || "ranger"}
                        size="small"
                        color={ranger.role === "admin" ? "error" : ranger.role === "supervisor" ? "warning" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRangers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchQuery ? "No rangers match your search" : "No rangers found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DashboardLayout>
    );
  }

  // Loading viewed profile
  if (loadingViewedProfile) {
    return (
      <DashboardLayout title="Ranger Profile">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <LinearProgress sx={{ width: 200 }} />
        </Box>
      </DashboardLayout>
    );
  }

  // Profile View
  return (
    <DashboardLayout title={isViewingOwnProfile ? "My Profile" : `${displayName}'s Profile`}>
      <Stack spacing={3}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToDirectory}
          sx={{ alignSelf: "flex-start" }}
        >
          Back to Directory
        </Button>

        {/* Hero Section */}
        <Paper sx={{ position: "relative", overflow: "hidden", borderRadius: 3 }}>
          {/* Background */}
          <Box
            ref={coverRef}
            onPointerDown={handleCoverPointerDown}
            onPointerMove={handleCoverPointerMove}
            onPointerUp={handleCoverPointerUp}
            sx={{
              height: { xs: 200, sm: 280 },
              backgroundColor: "grey.800",
              ...(!displayProfile?.backgroundPhotoDataUrl && {
                backgroundImage: `linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #7c3aed 100%)`,
              }),
              position: "relative",
              overflow: "hidden",
              cursor: repositioning ? "grab" : "default",
              userSelect: repositioning ? "none" : "auto",
              touchAction: repositioning ? "none" : "auto",
              ...(repositioning && dragStartY !== null && { cursor: "grabbing" }),
            }}
          >
            {displayProfile?.backgroundPhotoDataUrl && (
              <Box
                component="img"
                ref={coverImgRef}
                src={displayProfile.backgroundPhotoDataUrl}
                alt="Profile cover"
                draggable={false}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "cover",
                  objectPosition: `center ${coverPosY}%`,
                  pointerEvents: "none",
                }}
              />
            )}
            {/* Reposition overlay */}
            {repositioning && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0,0,0,0.35)",
                  pointerEvents: "none",
                }}
              >
                <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600, textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
                  Drag to reposition
                </Typography>
              </Box>
            )}
            {/* Cover action buttons - only for own profile */}
            {isViewingOwnProfile && !repositioning && (
              <Stack direction="row" spacing={1} sx={{ position: "absolute", top: 16, right: 16 }}>
                {displayProfile?.backgroundPhotoDataUrl && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<OpenWithIcon />}
                    onClick={handleStartReposition}
                    sx={{ backgroundColor: "rgba(0,0,0,0.5)", "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" } }}
                  >
                    Reposition
                  </Button>
                )}
                <label htmlFor="profile-background-input">
                  <input
                    id="profile-background-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageUpload("backgroundPhotoDataUrl")}
                  />
                  <Button
                    variant="contained"
                    component="span"
                    size="small"
                    startIcon={<CameraAltIcon />}
                    sx={{ backgroundColor: "rgba(0,0,0,0.5)", "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" } }}
                  >
                    Cover
                  </Button>
                </label>
              </Stack>
            )}
            {/* Save / Cancel buttons during reposition */}
            {repositioning && (
              <Stack direction="row" spacing={1} sx={{ position: "absolute", bottom: 16, right: 16 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveReposition}
                  sx={{ backgroundColor: "rgba(0,0,0,0.6)", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }}
                >
                  Save Position
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelReposition}
                  sx={{ backgroundColor: "rgba(0,0,0,0.6)", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Box>

          {/* Profile Info */}
          <Box sx={{ px: { xs: 2, sm: 4 }, pb: 3, pt: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "center", md: "flex-start" }}>
              {/* Avatar */}
              <Box sx={{ position: "relative", mt: { xs: -10, sm: -12 } }}>
                <Avatar
                  src={displayProfile?.profilePhotoDataUrl}
                  alt={displayName}
                  sx={{
                    width: { xs: 120, sm: 160 },
                    height: { xs: 120, sm: 160 },
                    border: "4px solid",
                    borderColor: "background.paper",
                    fontSize: "3rem",
                    boxShadow: 4,
                    flexShrink: 0,
                  }}
                >
                  {displayName.slice(0, 2).toUpperCase()}
                </Avatar>
                {isViewingOwnProfile && (
                  <label htmlFor="profile-avatar-input">
                    <input
                      id="profile-avatar-input"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleImageUpload("profilePhotoDataUrl")}
                    />
                    <IconButton
                      component="span"
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        boxShadow: 2,
                        "&:hover": { backgroundColor: "primary.dark" },
                      }}
                    >
                      <CameraAltIcon fontSize="small" />
                    </IconButton>
                  </label>
                )}
              </Box>

              {/* Name & Info */}
              <Box sx={{ flexGrow: 1, textAlign: { xs: "center", md: "left" }, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "center", md: "flex-start" }} flexWrap="wrap">
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {displayName}
                  </Typography>
                  {displayEarnedAwards.length >= 5 && !Array.isArray(displayEarnedAwards[0]) && (
                    <Tooltip title="Verified Ranger">
                      <VerifiedIcon color="primary" />
                    </Tooltip>
                  )}
                </Stack>
                {displayCallSign && (
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {displayCallSign} • {displayDivision}
                  </Typography>
                )}
                {displayProfile?.motto && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }} noWrap={false}>
                    "{displayProfile.motto}"
                  </Typography>
                )}
                {displayProfile?.bio && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 600, wordBreak: "break-word" }}>
                    {displayProfile.bio}
                  </Typography>
                )}
              </Box>

              {/* Edit Button - only for own profile */}
              {isViewingOwnProfile && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleOpenEditProfile}
                  sx={{ alignSelf: { xs: "center", md: "flex-start" }, mt: { xs: 0, md: 2 } }}
                >
                  Edit Profile
                </Button>
              )}
            </Stack>

            {/* Stats Row */}
            <Stack
              direction="row"
              spacing={{ xs: 2, sm: 4 }}
              justifyContent={{ xs: "center", md: "flex-start" }}
              sx={{ mt: 2 }}
            >
              {[
                { label: "Awards", value: displayEarnedAwards.length, icon: <EmojiEventsIcon fontSize="small" /> },
                { label: "Vehicles", value: displayVehicles.length, icon: <DirectionsCarIcon fontSize="small" /> },
                { label: "Equipment", value: displayEquipment.length, icon: <BuildIcon fontSize="small" /> },
                ...(isViewingOwnProfile ? [{ label: "Training", value: `${trainingPercent}%`, icon: <SchoolIcon fontSize="small" /> }] : []),
              ].map((stat) => (
                <Stack key={stat.label} alignItems="center" spacing={0.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {stat.icon}
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {busyField !== "none" && <LinearProgress sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }} />}
        </Paper>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab icon={<EmojiEventsIcon />} label="Awards" iconPosition="start" />
            <Tab icon={<DirectionsCarIcon />} label="Vehicles" iconPosition="start" />
            <Tab icon={<BuildIcon />} label="Equipment" iconPosition="start" />
            <Tab icon={<SchoolIcon />} label="Training" iconPosition="start" />
            <Tab icon={<PersonIcon />} label="About" iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Awards Tab */}
            {activeTab === 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Awards & Commendations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isViewingOwnProfile ? "Your achievements and recognitions" : `${displayName}'s achievements and recognitions`}
                    </Typography>
                  </Box>
                  {user?.role === "admin" && selectedRangerId && (
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAwardDialogOpen(true)}>
                      Manage Awards
                    </Button>
                  )}
                </Stack>

                {displayEarnedAwards.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                    <EmojiEventsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                    <Typography color="text.secondary">
                      {isViewingOwnProfile ? "No awards earned yet." : "No awards earned yet."}
                    </Typography>
                  </Paper>
                ) : (
                  (() => {
                    // Group awards by year (most recent first)
                    const byYear = {};
                    displayEarnedAwards.forEach((entry) => {
                      const yr = entry.year || 2025;
                      if (!byYear[yr]) byYear[yr] = [];
                      byYear[yr].push(entry);
                    });
                    const sortedYears = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));
                    return (
                      <Stack spacing={3}>
                        {sortedYears.map((yr) => (
                          <Box key={yr}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>{yr}</Typography>
                              <Divider sx={{ flexGrow: 1 }} />
                              <Chip label={`${byYear[yr].length} award${byYear[yr].length !== 1 ? "s" : ""}`} size="small" />
                            </Stack>
                            <Grid container spacing={2}>
                              {byYear[yr].map((entry) => {
                                const award = getAwardDetails(entry.awardId);
                                return (
                                  <Grid item xs={6} sm={4} md={3} key={entry.awardId}>
                                    <Card
                                      variant="outlined"
                                      sx={{
                                        textAlign: "center",
                                        p: 2,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "all 0.2s",
                                        "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                                      }}
                                    >
                                      <Typography variant="h2" sx={{ mb: 1 }}>
                                        {award.icon}
                                      </Typography>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {award.title}
                                      </Typography>
                                      {award.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                          {award.description}
                                        </Typography>
                                      )}
                                      {entry.notes && (
                                        <Typography variant="caption" sx={{ mt: 0.5, fontStyle: "italic", color: "primary.main" }}>
                                          {entry.notes}
                                        </Typography>
                                      )}
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        ))}
                      </Stack>
                    );
                  })()
                )}
              </Box>
            )}

            {/* Vehicles Tab */}
            {activeTab === 1 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Service Vehicles
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isViewingOwnProfile ? "Your operational platforms and response vehicles" : `${displayName}'s response vehicles`}
                    </Typography>
                  </Box>
                  {isViewingOwnProfile && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setVehicleForm({ ...EMPTY_VEHICLE_FORM });
                        setEditingVehicleId(null);
                        setVehicleError("");
                        setVehicleDialogOpen(true);
                      }}
                    >
                      Add Vehicle
                    </Button>
                  )}
                </Stack>

                {displayVehicles.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                    <DirectionsCarIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                    <Typography color="text.secondary">
                      {isViewingOwnProfile ? "No vehicles added yet. Add your service vehicle to showcase it on your profile." : "No vehicles displayed."}
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {displayVehicles.map((vehicle) => (
                      <Grid item xs={12} md={6} key={vehicle.id}>
                        <Card variant="outlined" sx={{ overflow: "hidden" }}>
                          {vehicle.photoDataUrl ? (
                            <Box
                              sx={{
                                height: 200,
                                backgroundImage: `url(${vehicle.photoDataUrl})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 200,
                                background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <DirectionsCarIcon sx={{ fontSize: 64, color: "grey.600" }} />
                            </Box>
                          )}
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {[vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ") || "Unlabeled Vehicle"}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                  {vehicle.color && <Chip label={vehicle.color} size="small" />}
                                  {vehicle.mileage && <Chip label={`${vehicle.mileage} mi`} size="small" variant="outlined" />}
                                  {vehicle.licensePlate && <Chip label={vehicle.licensePlate} size="small" variant="outlined" />}
                                </Stack>
                              </Box>
                              {isViewingOwnProfile && (
                                <Stack direction="row" spacing={0.5}>
                                  <IconButton
                                    color="primary"
                                    onClick={() => {
                                      setVehicleForm({ ...EMPTY_VEHICLE_FORM, ...vehicle });
                                      setEditingVehicleId(vehicle.id);
                                      setVehicleError("");
                                      setVehicleDialogOpen(true);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton color="error" onClick={handleRemoveVehicle(vehicle.id)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Stack>
                              )}
                            </Stack>
                            {vehicle.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                {vehicle.notes}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Equipment Tab */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Equipment & Capabilities
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {isViewingOwnProfile ? "Toggle the kits and capabilities you bring to ranger deployments" : `${displayName}'s equipment and capabilities`}
                </Typography>

                <Grid container spacing={2}>
                  {EQUIPMENT_OPTIONS.map((option) => {
                    const isActive = displayEquipment.includes(option.value);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={option.value}>
                        <Card
                          variant="outlined"
                          onClick={isViewingOwnProfile ? handleEquipmentToggle(option.value) : undefined}
                          sx={{
                            p: 2,
                            cursor: isViewingOwnProfile ? "pointer" : "default",
                            transition: "all 0.2s",
                            borderColor: isActive ? "primary.main" : "divider",
                            backgroundColor: isActive ? "action.selected" : "transparent",
                            "&:hover": isViewingOwnProfile ? { borderColor: "primary.main" } : {},
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: isActive ? "primary.main" : "action.hover",
                                color: isActive ? "primary.contrastText" : "text.secondary",
                              }}
                            >
                              {option.icon}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {option.label}
                              </Typography>
                            </Box>
                            {isViewingOwnProfile && <Checkbox checked={isActive} color="primary" />}
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Training Tab - own profile shows all statuses, other profiles show approved only */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Training & Certifications
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Certifications and courses synced from RangerAcademy
                </Typography>

                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {completedCourseCount} of {totalCourses} courses complete
                    </Typography>
                    <Chip label={`${trainingPercent}%`} color="primary" />
                  </Stack>
                  <LinearProgress variant="determinate" value={trainingPercent} sx={{ height: 12, borderRadius: 6 }} />
                </Paper>

                {trainingCertifications.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                    <SchoolIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                    <Typography color="text.secondary">
                      No certifications uploaded yet. Complete courses in RangerAcademy to build your transcript.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {trainingCertifications.map((cert) => {
                      const course = trainingModules.find((m) => m.id === cert.courseId);
                      const statusColor =
                        cert.status === "approved" ? "success" : cert.status === "pending" ? "warning" : "error";
                      const statusLabel =
                        cert.status === "approved" ? "Approved" : cert.status === "pending" ? "Pending Review" : "Rejected";
                      return (
                        <Grid item xs={12} sm={6} md={4} key={cert.id}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <StarIcon color={cert.status === "approved" ? "warning" : "disabled"} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                  {course?.title || cert.courseName}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={statusLabel} color={statusColor} size="small" />
                                {cert.completionDate && (
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(cert.completionDate).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Stack>
                              {cert.status === "rejected" && cert.reviewNotes && (
                                <Typography variant="caption" color="error.main">
                                  Note: {cert.reviewNotes}
                                </Typography>
                              )}
                              <Button
                                size="small"
                                onClick={async () => {
                                  try {
                                    const res = await api.get(`/training-certifications/${cert.id}/download`);
                                    window.open(res.data.url, "_blank");
                                  } catch {
                                    setSnackbar({ open: true, message: "Failed to open certificate", severity: "error" });
                                  }
                                }}
                              >
                                View PDF
                              </Button>
                            </Stack>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}

            {/* About Tab - tab index is always 4 now */}
            {activeTab === 4 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  {isViewingOwnProfile ? "About Me" : `About ${displayName}`}
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      {[
                        { label: "Display Name", value: displayName },
                        { label: "Callsign", value: displayCallSign || "Not set" },
                        { label: "Division", value: displayDivision },
                        ...(isViewingOwnProfile ? [
                          { label: "Email", value: profile.email || user?.email || "Not provided" },
                          { label: "Phone", value: profile.phoneNumber || user?.phoneNumber || "Not provided" },
                        ] : []),
                        { label: "Member Since", value: displayProfile?.joinDate || "Not recorded" },
                      ].map((item) => (
                        <Box key={item.label}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {item.label.toUpperCase()}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      BIO
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {profile.bio || "No bio added yet. Click Edit Profile to add one."}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileOpen} onClose={() => setEditProfileOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Display Name"
              fullWidth
              value={profileForm.displayName}
              onChange={(e) => setProfileForm((p) => ({ ...p, displayName: e.target.value }))}
            />
            <TextField
              label="Ranger ID"
              helperText="Your call sign and unit number use this same ID."
              fullWidth
              value={profileForm.callSign}
              onChange={(e) => setProfileForm((p) => ({ ...p, callSign: e.target.value }))}
            />
            <TextField
              label="Motto"
              fullWidth
              placeholder="Your personal motto or tagline"
              value={profileForm.motto}
              onChange={(e) => setProfileForm((p) => ({ ...p, motto: e.target.value }))}
            />
            <TextField
              label="Phone"
              fullWidth
              value={profileForm.phoneNumber}
              onChange={(e) => setProfileForm((p) => ({ ...p, phoneNumber: e.target.value }))}
            />
            <TextField
              label="Email"
              fullWidth
              value={profileForm.email}
              onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
            />
            <TextField
              label="Member Since"
              fullWidth
              placeholder="e.g., January 2024"
              value={profileForm.joinDate}
              onChange={(e) => setProfileForm((p) => ({ ...p, joinDate: e.target.value }))}
            />
            <TextField
              label="Bio"
              fullWidth
              multiline
              minRows={3}
              placeholder="Tell other rangers about yourself..."
              value={profileForm.bio}
              onChange={(e) => setProfileForm((p) => ({ ...p, bio: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProfileOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={savingProfile}>
            {savingProfile ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Dialog */}
      <Dialog open={vehicleDialogOpen} onClose={() => setVehicleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVehicleId ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {vehicleForm.photoDataUrl && (
              <Box
                sx={{
                  height: 150,
                  backgroundImage: `url(${vehicleForm.photoDataUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: 2,
                }}
              />
            )}
            <label htmlFor="vehicle-photo-input">
              <input
                id="vehicle-photo-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleVehiclePhotoUpload}
              />
              <Button variant="outlined" component="span" fullWidth startIcon={<CameraAltIcon />}>
                {vehicleForm.photoDataUrl ? "Change Photo" : "Add Vehicle Photo"}
              </Button>
            </label>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  label="Year"
                  fullWidth
                  value={vehicleForm.year}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, year: e.target.value }))}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Make"
                  fullWidth
                  value={vehicleForm.make}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, make: e.target.value }))}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Model"
                  fullWidth
                  value={vehicleForm.model}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, model: e.target.value }))}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Trim"
                  fullWidth
                  value={vehicleForm.trim || ""}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, trim: e.target.value }))}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Color"
                  fullWidth
                  value={vehicleForm.color}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, color: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Mileage"
                  fullWidth
                  value={vehicleForm.mileage}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, mileage: e.target.value }))}
                />
              </Grid>
            </Grid>
            <TextField
              label="License Plate"
              fullWidth
              value={vehicleForm.licensePlate}
              onChange={(e) => setVehicleForm((p) => ({ ...p, licensePlate: e.target.value }))}
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              placeholder="Lighting package, comms, special equipment..."
              value={vehicleForm.notes}
              onChange={(e) => setVehicleForm((p) => ({ ...p, notes: e.target.value }))}
            />
            {vehicleError && <Alert severity="error">{vehicleError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveVehicle}>
            {editingVehicleId ? "Update Vehicle" : "Add Vehicle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Award Management Dialog */}
      <Dialog open={awardDialogOpen} onClose={() => { setAwardDialogOpen(false); setGrantingAwardId(null); setGrantNotes(""); }} maxWidth="md" fullWidth>
        <DialogTitle>Manage Awards — {viewedProfile?.displayName || viewedProfile?.username || "Ranger"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Grant or revoke awards for this ranger. Set the year and optional notes before granting.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="Year"
              type="number"
              size="small"
              value={grantYear}
              onChange={(e) => setGrantYear(Number(e.target.value))}
              sx={{ width: 120 }}
              inputProps={{ min: 2020, max: 2099 }}
            />
            <TextField
              label="Notes (optional)"
              size="small"
              fullWidth
              value={grantNotes}
              onChange={(e) => setGrantNotes(e.target.value)}
              placeholder="e.g. Awarded for Hurricane Milton response"
            />
          </Stack>
          <Grid container spacing={1}>
            {ALL_AWARDS.map((award) => {
              const viewedEarned = viewedProfile?.earnedAwards || [];
              const earnedEntry = viewedEarned.find((e) => e.awardId === award.id);
              const isEarned = !!earnedEntry;
              return (
                <Grid item xs={6} sm={4} md={3} key={award.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      textAlign: "center",
                      transition: "all 0.2s",
                      borderColor: isEarned ? "success.main" : "divider",
                      backgroundColor: isEarned ? "action.selected" : "transparent",
                    }}
                  >
                    <Typography variant="h5">{award.icon}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: isEarned ? 700 : 400 }}>
                      {award.title}
                    </Typography>
                    {isEarned && (
                      <Typography variant="caption" display="block" color="success.main" sx={{ fontWeight: 600 }}>
                        Earned {earnedEntry.year}
                      </Typography>
                    )}
                    {isEarned && earnedEntry.notes && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        {earnedEntry.notes}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                      {isEarned ? (
                        <Button size="small" color="error" variant="outlined" onClick={() => handleRevokeAward(award.id)}>
                          Revoke
                        </Button>
                      ) : (
                        <Button size="small" color="primary" variant="contained" onClick={() => handleGrantAward(award.id, grantYear, grantNotes)}>
                          Grant
                        </Button>
                      )}
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAwardDialogOpen(false); setGrantingAwardId(null); setGrantNotes(""); }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
